import { Logo } from "@/components/common/Logo"
import { Button } from "@/components/ui/Button"
import { NAV_ITEMS } from "@/data"
import { useScrollLock } from "@/hooks/useScrollLock"
import { cn } from "@/lib/utils"
import { Link, useLocation } from "@tanstack/react-router"
import { AnimatePresence, motion } from "framer-motion"
import { ArrowRight, ArrowUpRight, ChevronDown, Menu, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

function MegaDropdown({
  item,
  isOpen,
  onClose,
  onMouseEnter,
  onMouseLeave,
}: {
  item: (typeof NAV_ITEMS)[0]
  isOpen: boolean
  onClose: () => void
  onMouseEnter: () => void
  onMouseLeave: () => void
}) {
  if (!item.dropdown) return null
  const columns = item.dropdown.columns

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="bg-ink/10 fixed inset-0 z-40 backdrop-blur-[1.5px]"
            style={{ top: "var(--nav-height, 80px)" }}
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 left-0 z-50"
            style={{ top: "var(--nav-height, 80px)" }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            <div className="border-border border-b bg-white shadow-[0_20px_60px_-10px_rgba(8,8,16,0.12)]">
              <div className="mx-auto max-w-7xl px-5 pt-8 pb-0 sm:px-6 lg:px-8">
                {/* Column grid */}
                <div
                  className={cn(
                    "grid gap-x-0",
                    columns.length === 2 && "grid-cols-2 lg:grid-cols-[1fr_1fr_300px]",
                    columns.length === 3 && "grid-cols-2 lg:grid-cols-[repeat(3,1fr)_280px]",
                    columns.length >= 4 &&
                      "grid-cols-2 lg:grid-cols-4 xl:grid-cols-[repeat(4,1fr)_260px]",
                  )}
                >
                  {columns.map((column, i) => (
                    <div
                      key={i}
                      className={cn(
                        "px-8 first:pl-0",
                        i < columns.length - 1 && "border-border border-r",
                      )}
                    >
                      {/* Column heading */}
                      <h4 className="text-ink mb-2 pb-2 text-base font-semibold tracking-tight">
                        {column.title}
                      </h4>

                      <ul className="space-y-0.5 pb-8 text-sm">
                        {column.links.map((link) => (
                          <li key={link.href}>
                            <Link
                              to={link.href}
                              className="group/link -mx-3 flex items-center gap-2 rounded-lg px-3 py-2.5 transition-colors duration-100"
                              onClick={onClose}
                            >
                              <div className="min-w-0 flex-1">
                                <div className="relative flex items-center gap-1">
                                  <span className="text-neutral-700 transition-colors duration-150 group-hover/link:text-indigo-700">
                                    {link.label}
                                  </span>
                                  <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-indigo-500/60 transition-all group-hover/link:w-[50%]" />
                                  <ArrowUpRight className="h-4 w-4 shrink-0 -translate-x-0.5 translate-y-0.5 text-indigo-500 opacity-0 transition-all duration-200 group-hover/link:translate-x-0 group-hover/link:translate-y-0 group-hover/link:opacity-100" />
                                </div>
                                {(link as any).description && (
                                  <p className="text-muted mt-0.5 line-clamp-1 leading-relaxed">
                                    {(link as any).description}
                                  </p>
                                )}
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}

                  {/* CTA card — always rightmost */}
                  <div className="border-border hidden border-l pb-8 pl-8 lg:block">
                    <div className="bg-ink noise relative flex h-full min-h-[190px] flex-col justify-between overflow-hidden rounded-xl p-6">
                      {/* Dot grid overlay */}
                      <div className="dot-bg pointer-events-none absolute inset-0 opacity-[0.08]" />
                      {/* Blue glow */}
                      <div className="bg-blue/25 pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full blur-3xl" />

                      <div className="relative">
                        <span className="text-blue-mid mb-2.5 block text-[10px] font-bold tracking-[0.16em] uppercase">
                          Get started
                        </span>
                        <p className="font-display text-[16px] leading-snug text-white">
                          Not sure which solution fits?
                        </p>
                        <p className="mt-1.5 text-[12px] leading-relaxed text-white/50">
                          Talk to our experts for a personalised plan.
                        </p>
                      </div>

                      <Link
                        to="/contact"
                        onClick={onClose}
                        className="btn-shine bg-blue hover:bg-blue-dark relative mt-4 inline-flex w-fit items-center gap-1.5 rounded-lg px-4 py-2.5 text-[12.5px] font-semibold text-white transition-colors"
                      >
                        Talk to us <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
  const location = useLocation()
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null)
  const navRef = useRef<HTMLElement>(null)

  const anyOverlayOpen = !!activeDropdown || menuOpen
  useScrollLock(anyOverlayOpen)

  useEffect(() => {
    const nav = navRef.current
    if (!nav) return
    const sync = () =>
      document.documentElement.style.setProperty(
        "--nav-height",
        `${nav.getBoundingClientRect().height}px`,
      )
    sync()
    window.addEventListener("resize", sync)
    return () => window.removeEventListener("resize", sync)
  }, [])

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20)
    window.addEventListener("scroll", handler, { passive: true })
    return () => window.removeEventListener("scroll", handler)
  }, [])

  // Close everything on route change
  useEffect(() => {
    setActiveDropdown(null)
    setMenuOpen(false)
  }, [location.pathname])

  const showDropdown = useCallback((label: string) => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    setActiveDropdown(label)
  }, [])

  const hideDropdown = useCallback(() => {
    hideTimerRef.current = setTimeout(() => setActiveDropdown(null), 180)
  }, [])

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
  }, [])

  const isSpecialPage = ["/pricing", "/compliance", "/partners"].includes(location.pathname)
  const solidBg = scrolled || isSpecialPage || !!activeDropdown

  return (
    <>
      <header
        ref={navRef}
        className={cn(
          "fixed inset-x-0 top-0 z-50 transition-all duration-300",
          solidBg
            ? "border-border border-b bg-white/[0.97] shadow-[0_1px_0_0_rgba(8,8,16,0.04)] backdrop-blur-xl"
            : "bg-transparent",
        )}
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between lg:h-20">
            <Logo
              size="md"
              imgClassName="w-7 h-7 lg:w-14 lg:h-14"
              className="[&>div]:h-7 [&>div]:w-7 lg:[&>div]:h-14 lg:[&>div]:w-14"
            />

            <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Main navigation">
              {NAV_ITEMS.map((item) => {
                const isOpen = activeDropdown === item.label
                const hasDropdown = !!item.dropdown

                return (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => hasDropdown && showDropdown(item.label)}
                    onMouseLeave={hideDropdown}
                  >
                    {hasDropdown ? (
                      <button
                        type="button"
                        onClick={() => setActiveDropdown(isOpen ? null : item.label)}
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[13.5px] font-medium transition-all duration-150",
                          isOpen
                            ? "text-blue bg-blue-light"
                            : "text-ink/70 hover:text-ink hover:bg-surface",
                        )}
                        aria-expanded={isOpen}
                        aria-haspopup="true"
                      >
                        {item.label}
                        <ChevronDown
                          className={cn(
                            "h-3.5 w-3.5 transition-transform duration-200",
                            isOpen ? "text-blue rotate-180" : "text-muted",
                          )}
                        />
                      </button>
                    ) : (
                      <Link
                        to={item.href}
                        className="inline-flex items-center rounded-lg px-3.5 py-2 text-[13.5px] font-medium transition-all duration-150"
                        activeProps={{ className: "text-blue bg-blue-light" }}
                        inactiveProps={{ className: "text-ink/70 hover:text-ink hover:bg-surface" }}
                      >
                        {item.label}
                      </Link>
                    )}

                    <MegaDropdown
                      item={item}
                      isOpen={isOpen}
                      onClose={() => setActiveDropdown(null)}
                      onMouseEnter={cancelHide}
                      onMouseLeave={hideDropdown}
                    />
                  </div>
                )
              })}
            </nav>

            <div className="hidden items-center gap-2 lg:flex">
              <Button
                variant="ghost"
                size="sm"
                className="text-ink/70 hover:text-ink hover:bg-surface rounded-lg px-4 text-[13.5px] font-medium"
                asChild
              >
                <Link
                  // @ts-expect-error - Login route exists in the host phoenix app
                  to="/login"
                >
                  Sign in
                </Link>
              </Button>

              <Button
                size="sm"
                className="btn-shine bg-blue hover:bg-blue-dark rounded-full px-5 text-[13.5px] font-semibold text-white shadow-[0_4px_16px_rgba(24,71,240,0.28)] transition-all duration-200 hover:shadow-[0_6px_20px_rgba(24,71,240,0.36)]"
                asChild
              >
                <Link to="/contact">
                  Get Started <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>

            {/* ── Mobile hamburger ── */}
            <button
              className={cn(
                "rounded-xl p-2 transition-colors lg:hidden",
                menuOpen ? "bg-surface" : "hover:bg-surface",
              )}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-expanded={menuOpen}
              aria-label="Toggle navigation"
            >
              <AnimatePresence mode="wait" initial={false}>
                {menuOpen ? (
                  <motion.span
                    key="x"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="block"
                  >
                    <X className="text-ink h-5 w-5" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="block"
                  >
                    <Menu className="text-ink h-5 w-5" />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 flex flex-col bg-white lg:hidden"
            style={{ paddingTop: "var(--nav-height, 64px)" }}
          >
            {/* Scrollable nav list */}
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-0.5 px-4 py-5">
                {NAV_ITEMS.map((item, idx) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.04 + 0.06, duration: 0.22 }}
                  >
                    {item.dropdown ? (
                      <div>
                        <button
                          className={cn(
                            "flex w-full items-center justify-between rounded-xl px-4 py-3.5 text-[15px] font-semibold transition-colors",
                            activeDropdown === item.label
                              ? "text-blue bg-blue-light"
                              : "text-ink hover:bg-surface",
                          )}
                          onClick={() =>
                            setActiveDropdown(activeDropdown === item.label ? null : item.label)
                          }
                        >
                          {item.label}
                          <ChevronDown
                            className={cn(
                              "h-4 w-4 transition-transform duration-200",
                              activeDropdown === item.label ? "text-blue rotate-180" : "text-muted",
                            )}
                          />
                        </button>

                        <AnimatePresence>
                          {activeDropdown === item.label && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{
                                duration: 0.22,
                                ease: [0.22, 1, 0.36, 1],
                              }}
                              className="overflow-hidden"
                            >
                              <div className="border-blue-mid/40 mt-1 ml-4 space-y-5 border-l-2 py-3 pl-4">
                                {item.dropdown.columns.map((col, i) => (
                                  <div key={i}>
                                    <h5 className="text-muted mb-2 px-3 text-[10px] font-bold tracking-[0.13em] uppercase">
                                      {col.title}
                                    </h5>
                                    <ul className="space-y-0.5">
                                      {col.links.map((link) => (
                                        <li key={link.href}>
                                          <Link
                                            to={link.href}
                                            className="text-ink/80 hover:text-blue hover:bg-blue-light flex items-center gap-2 rounded-lg px-3 py-2.5 text-[13.5px] font-medium transition-colors"
                                            onClick={() => {
                                              setActiveDropdown(null)
                                              setMenuOpen(false)
                                            }}
                                          >
                                            {link.label}
                                          </Link>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        className="flex items-center rounded-xl px-4 py-3.5 text-[15px] font-semibold transition-colors"
                        activeProps={{ className: "text-blue bg-blue-light" }}
                        inactiveProps={{ className: "text-ink hover:bg-surface" }}
                        onClick={() => setMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Sticky CTA footer */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18, duration: 0.25 }}
              className="border-border space-y-2.5 border-t bg-white px-4 py-5"
            >
              <Button
                variant="outline"
                className="border-border text-ink hover:bg-surface h-12 w-full rounded-xl text-[14px] font-semibold"
                asChild
              >
                <Link
                  // @ts-expect-error - Login route exists in the host phoenix app
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                >
                  Sign in
                </Link>
              </Button>
              <Button
                className="btn-shine bg-blue hover:bg-blue-dark h-12 w-full rounded-xl text-[14px] font-semibold text-white shadow-[0_4px_16px_rgba(24,71,240,0.25)] transition-all"
                asChild
              >
                <Link to="/contact" onClick={() => setMenuOpen(false)}>
                  Get Started <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
